import {debounce} from '@github/mini-throttle'
import {CodeSquareIcon, FileDirectoryFillIcon, FileIcon, PaperclipIcon} from '@primer/octicons-react'
import {AnchoredOverlay, type AnchoredOverlayProps, Box} from '@primer/react'
import {ActionList} from '@primer/react'
import {SelectPanel} from '@primer/react/experimental'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {flushSync} from 'react-dom'

import {useFilterQuery} from '../hooks/use-filter-query'
import {useRepositoryFilesQuery} from '../hooks/use-repository-files-query'
import {useRepositorySymbolsQuery} from '../hooks/use-repository-symbols-query'
import {group as groupArray} from '../utils/array'
import {
  getSymbolName,
  isRepository,
  makeFileReference,
  makeFolderReference,
  makeSymbolReference,
  referenceID,
} from '../utils/copilot-chat-helpers'
import type {CopilotChatRepo} from '../utils/copilot-chat-types'
import {useChatState, useChatStateLens} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import styles from './ReferencesSelectPanel.module.css'
import {RepoSelectPanel} from './RepoSelectPanel'

const headerText = 'Select files, folders, and symbols'
const subheaderText = (repo: string) =>
  `Choose items from ${repo} to chat about. Use fewer references for more accurate responses.`
const filterPlaceholder = 'Search'

type TopicReferencesSelectPanelProps = Omit<ReferencesSelectPanelProps, 'sourceRepository'>

/** Uses the currently selected topic repository as the source repo for the references. */
export const TopicReferencesSelectPanel = ({open, onOpenChange, ...props}: TopicReferencesSelectPanelProps) => {
  const {currentTopic} = useChatState()

  if (!isRepository(currentTopic)) {
    return <ChooseRepositoryInstruction open={open} setOpen={onOpenChange} />
  }

  return <ReferencesSelectPanel {...props} open={open} onOpenChange={onOpenChange} sourceRepository={currentTopic} />
}

type MultistepReferencesSelectPanelProps = TopicReferencesSelectPanelProps

/** First shows a repo select panel, then selects references from that repo. */
export const MultistepReferencesSelectPanel = ({
  open,
  onOpenChange,
  cancelReturnFocusRef,
  submitReturnFocusRef,
  ...props
}: MultistepReferencesSelectPanelProps) => {
  const manager = useChatManager()
  const [repo, setRepo] = useState<CopilotChatRepo>()

  useEffect(() => setRepo(undefined), [open])

  const references = useChatStateLens(s => s.currentReferences)
  const repoReferenceIds = new Set(references.filter(r => r.type === 'repository').map(r => r.id))

  const [reposError, setReposError] = useState<string>()

  const selectingRepo = useRef(false)
  const onSelectRepo = async (repoId: number) => {
    setReposError(undefined)

    selectingRepo.current = true
    const repoResponse = await manager.service.fetchRepo(repoId)

    if (!repoResponse.ok) {
      setReposError('Error: failed to load repository details. Please try again.')
    } else {
      setRepo(repoResponse.payload)
    }

    selectingRepo.current = false
  }

  if (!repo)
    return (
      <RepoSelectPanel
        selectionVariant="instant"
        open={open}
        onOpenChange={newOpen => {
          // Ignore while loading repo data in order to avoid flash
          if (!selectingRepo.current) onOpenChange(newOpen)
        }}
        selectedRepoIds={new Set()}
        onSelectRepo={onSelectRepo}
        cancelReturnFocusRef={cancelReturnFocusRef}
        submitReturnFocusRef={submitReturnFocusRef}
        description="Choose a repository to browse for files and symbols."
        featuredRepoIds={repoReferenceIds}
        error={reposError}
      />
    )

  return (
    <ReferencesSelectPanel
      {...props}
      open={open}
      onOpenChange={onOpenChange}
      sourceRepository={repo}
      cancelReturnFocusRef={cancelReturnFocusRef}
      submitReturnFocusRef={submitReturnFocusRef}
    />
  )
}

interface ReferencesSelectPanelProps {
  renderAnchor?: AnchoredOverlayProps['renderAnchor']
  anchorRef?: RefObject<HTMLButtonElement>
  open: boolean
  onOpenChange: (val: boolean) => void
  submitReturnFocusRef: RefObject<HTMLElement>
  cancelReturnFocusRef: RefObject<HTMLElement>
  /** Repository from which to select files, folders, and symbols. */
  sourceRepository: CopilotChatRepo
}

const ReferencesSelectPanel = ({
  renderAnchor,
  anchorRef,
  open,
  onOpenChange,
  submitReturnFocusRef,
  cancelReturnFocusRef,
  sourceRepository,
}: ReferencesSelectPanelProps) => {
  const [query, setQuery] = useState('')
  const debouncedSetQuery = useRef(debounce(setQuery, 250))

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const {initialLoading, filterLoading, selected, unselected, toggleSelection} = useReferencesData(
    open,
    query,
    sourceRepository,
  )

  const totalResults = selected.length + unselected.length

  const nwo = `${sourceRepository.ownerLogin}/${sourceRepository.name}`

  return (
    <Box
      sx={{
        flexGrow: '0 !important',
      }}
    >
      {renderAnchor?.({})}
      {/* eslint-disable-next-line primer-react/no-system-props */}
      <SelectPanel
        anchorRef={anchorRef}
        title={headerText}
        description={subheaderText(nwo)}
        onCancel={() => {
          // eslint-disable-next-line @eslint-react/dom/no-flush-sync
          flushSync(() => onOpenChange(false))
          submitReturnFocusRef.current?.focus()
        }}
        onSubmit={() => {
          // eslint-disable-next-line @eslint-react/dom/no-flush-sync
          flushSync(() => onOpenChange(false))
          cancelReturnFocusRef.current?.focus()
        }}
        open={open}
        selectionVariant="multiple"
        variant="modal"
        width="large"
        maxHeight="xlarge"
      >
        <SelectPanel.Header>
          <SelectPanel.SearchInput
            loading={filterLoading}
            onChange={e => debouncedSetQuery.current(e.target.value)}
            placeholder={filterPlaceholder}
            aria-label={filterPlaceholder}
          />
        </SelectPanel.Header>
        <ActionList sx={{minHeight: 340}}>
          {initialLoading ? (
            <SelectPanel.Loading>Fetching files, folders, and symbols&hellip;</SelectPanel.Loading>
          ) : totalResults === 0 && !filterLoading ? (
            <SelectPanel.Message variant="empty" title="No files or symbols found">
              Try a different search term
            </SelectPanel.Message>
          ) : (
            <>
              {selected.map(item => (
                <ReferenceListItem key={itemId(item)} item={item} selected toggleSelection={toggleSelection} />
              ))}
              {selected.length > 0 && unselected.length > 0 && <ActionList.Divider />}
              {unselected.map(item => (
                <ReferenceListItem key={itemId(item)} item={item} selected={false} toggleSelection={toggleSelection} />
              ))}
            </>
          )}
        </ActionList>
      </SelectPanel>
    </Box>
  )
}

function ChooseRepositoryInstruction({
  open,
  setOpen,
  renderAnchor,
}: {
  open: boolean
  setOpen: (v: boolean) => void
  renderAnchor?: AnchoredOverlayProps['renderAnchor']
}) {
  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderAnchor={p => renderAnchor?.(p) ?? <></>}
    >
      <div className={styles.chooseRepoInstructionPanel}>
        <PaperclipIcon />
        <h3 className={styles.chooseRepoTitle}>Attach a repository first</h3>
        <p className={styles.chooseRepoSubtitle}>
          To pick attachments, attach a repository where the files are stored.
        </p>
      </div>
    </AnchoredOverlay>
  )
}

const itemId = (item: Item) => `${item.type}#${item.text}`

function ReferenceListItem({
  toggleSelection,
  selected,
  item,
}: {
  toggleSelection: (item: Item) => void
  selected: boolean
  item: Item
}) {
  return (
    <ActionList.Item
      onSelect={() => {
        toggleSelection(item)
        // this item is going to get moved into or out of the selected group on screen, and the re-render will cause it to lose focus so we have to restore it
        setTimeout(() => {
          document.getElementById(itemId(item))?.focus()
        })
      }}
      selected={selected}
      aria-checked={selected}
      id={itemId(item)}
      sx={{
        mx: 2,
        '&[data-is-active-descendant="activated-directly"]': {
          backgroundColor: 'transparent',
          outline: '2px solid var(--focus-outlineColor, var(--color-accent-emphasis))',
          outlineOffset: '-2px',
        },
      }}
    >
      <ActionList.LeadingVisual>
        {item.type === 'symbol' ? (
          <CodeSquareIcon />
        ) : item.type === 'folder' ? (
          <FileDirectoryFillIcon />
        ) : (
          <FileIcon />
        )}
      </ActionList.LeadingVisual>
      <Box
        sx={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {item.text}
      </Box>
    </ActionList.Item>
  )
}

interface Item {
  type: 'file' | 'folder' | 'symbol'
  text: string
}

const DISPLAYED_MATCHES_LIMIT = 7

/**
 * useReferencesData handles managing the user's selection, and
 * applying that selection to the chat state.
 */
function useReferencesData(open: boolean, query: string, repo: CopilotChatRepo | null) {
  const state = useChatState()
  const manager = useChatManager()
  const queriedItemsResult = useQueriedItems(query, repo, open)

  const symbols = useRepositorySymbolsQuery(open ? repo : null, query).data

  const [selectedFiles, selectedFolders, selectedSymbols] = useMemo(() => {
    const selectedFilesSet = new Set<string>()
    const selectedFoldersSet = new Set<string>()
    const selectedSymbolsSet = new Set<string>()

    for (const reference of state.currentReferences) {
      if (reference.type === 'file' && reference.repoOwner === repo?.ownerLogin && reference.repoName === repo?.name) {
        selectedFilesSet.add(reference.path)
      } else if (
        reference.type === 'folder' &&
        reference.repoOwner === repo?.ownerLogin &&
        reference.repoName === repo?.name
      ) {
        selectedFoldersSet.add(reference.path)
      } else if (
        reference.type === 'symbol' &&
        reference.kind === 'suggestionSymbol' &&
        reference.suggestionDefinitions?.some(def => def.repoOwner === repo?.ownerLogin && def.repoName === repo?.name)
      ) {
        selectedSymbolsSet.add(reference.name)
      }
    }

    return [selectedFilesSet, selectedFoldersSet, selectedSymbolsSet]
  }, [state.currentReferences, repo?.ownerLogin, repo?.name])

  const {selected = [], unselected = []} = groupArray(queriedItemsResult.items, item => {
    switch (item.type) {
      case 'file':
        return selectedFiles.has(item.text) ? 'selected' : 'unselected'
      case 'folder':
        return selectedFolders.has(item.text) ? 'selected' : 'unselected'
      case 'symbol':
        return selectedSymbols.has(item.text) ? 'selected' : 'unselected'
    }
  })

  const toggleSelection = useCallback(
    (item: Item) => {
      if (!repo) return

      let reference
      switch (item.type) {
        case 'file':
          reference = makeFileReference(item.text, repo)
          break
        case 'folder':
          reference = makeFolderReference(item.text, repo)
          break
        case 'symbol':
          {
            const symbolSelection = symbols?.find(s => getSymbolName(s) === item.text)
            if (!symbolSelection) return
            reference = makeSymbolReference(symbolSelection, repo)
          }
          break
      }

      const id = referenceID(reference)
      const currentReference = state.currentReferences.find(ref => id === referenceID(ref))

      if (currentReference) {
        manager.removeReference(currentReference)
      } else {
        manager.addReference(reference, 'references-menu')
      }
    },
    [manager, repo, state.currentReferences, symbols],
  )

  return {
    ...queriedItemsResult,
    selected,
    unselected,
    toggleSelection,
  }
}

interface QueriedItemsResult {
  items: Item[]
  filterLoading: boolean
  initialLoading: boolean
}

/**
 * useQueriesItems handles fetching an appropriate list of items based on the user's query
 */
function useQueriedItems(query: string, repo: CopilotChatRepo | null, open: boolean): QueriedItemsResult {
  const queryRepo = open ? repo : null
  const {
    query: filesAndFoldersQuery,
    filteredPathsQuery,
    filteredDirectoriesQuery,
  } = useRepositoryFilesQuery(queryRepo, query)
  const symbolsQuery = useRepositorySymbolsQuery(queryRepo, query)

  const symbolNames = symbolsQuery.data?.map(s => getSymbolName(s)) ?? null

  const filteredSymbolsQuery = useFilterQuery(symbolNames, query)

  const fileItems: Item[] =
    filteredPathsQuery.data?.slice(0, DISPLAYED_MATCHES_LIMIT).map(path => ({type: 'file', text: path})) ?? []
  const folderItems: Item[] =
    filteredDirectoriesQuery.data?.slice(0, DISPLAYED_MATCHES_LIMIT).map(path => ({type: 'folder', text: path})) ?? []
  const symbolItems: Item[] =
    filteredSymbolsQuery.data?.slice(0, DISPLAYED_MATCHES_LIMIT).map(name => ({type: 'symbol', text: name})) ?? []

  const items = [...fileItems, ...folderItems, ...symbolItems]

  const initialLoading = filesAndFoldersQuery.isLoading && !query
  const filterLoading =
    !initialLoading &&
    (filesAndFoldersQuery.isLoading ||
      filteredPathsQuery.isLoading ||
      filteredDirectoriesQuery.isLoading ||
      symbolsQuery.isLoading ||
      filteredSymbolsQuery.isLoading)

  return {items, filterLoading, initialLoading}
}

try{ TopicReferencesSelectPanel.displayName ||= 'TopicReferencesSelectPanel' } catch {}
try{ MultistepReferencesSelectPanel.displayName ||= 'MultistepReferencesSelectPanel' } catch {}
try{ ReferencesSelectPanel.displayName ||= 'ReferencesSelectPanel' } catch {}
try{ ChooseRepositoryInstruction.displayName ||= 'ChooseRepositoryInstruction' } catch {}
try{ ReferenceListItem.displayName ||= 'ReferenceListItem' } catch {}